import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import 'font-awesome/css/font-awesome.min.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Login.css';
import Spinner from '../Components/Spinner';
import { getApiBase } from '../utils/authClient';
import { useLocale } from '../context/LocaleContext';

const toastOptions = {
  position: 'top-right',
  autoClose: 8000,
  pauseOnHover: true,
  draggable: true,
  theme: 'dark',
};

export default function ResetPassword() {
  const { t } = useLocale();
  const apiBase = getApiBase();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get('token') || '').trim();

  const [checking, setChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [loader, setLoader] = useState('none');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      setTokenValid(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${apiBase}/auth/reset-password/validate?token=${encodeURIComponent(token)}`
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setTokenValid(Boolean(data.valid));
      } catch {
        if (!cancelled) setTokenValid(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiBase, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t('auth.resetMinLength'), toastOptions);
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t('auth.resetMismatch'), toastOptions);
      return;
    }

    setLoader('flex');
    try {
      const res = await fetch(`${apiBase}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, confirmPassword }),
      });
      const data = await res.json().catch(() => ({}));
      setLoader('none');

      if (!res.ok || data.status === false) {
        toast.error(data.message || t('auth.resetFail'), toastOptions);
        return;
      }

      setDone(true);
      toast.success(data.message, toastOptions);
      setTimeout(() => navigate('/login_user', { replace: true }), 2500);
    } catch {
      setLoader('none');
      toast.error(t('auth.serverConnect'), toastOptions);
    }
  };

  return (
    <div className="login_split_page login_auth_simple">
      <main className="login_auth_simple_main">
        <aside className="login_split_panel login_auth_panel_only">
          <div className="login_split_panel_inner">
            <Link to="/login_user" className="login_auth_back">
              <i className="fa fa-arrow-left" aria-hidden="true" /> {t('auth.backToSignIn')}
            </Link>

            <h2 className="login_split_panel_title">{t('auth.resetTitle')}</h2>

            {checking ? (
              <p className="login_split_panel_subtitle">{t('auth.resetChecking')}</p>
            ) : null}

            {!checking && !tokenValid ? (
              <div className="login_auth_success" role="alert">
                <p>{t('auth.resetInvalid')}</p>
                <Link to="/forgot-password" className="login_form_button login_auth_success_btn">
                  {t('auth.resetRequestNew')}
                </Link>
              </div>
            ) : null}

            {!checking && tokenValid && !done ? (
              <>
                <p className="login_split_panel_subtitle">{t('auth.resetSubtitle')}</p>
                <form className="login_u_form" onSubmit={handleSubmit} noValidate>
                  <div className="login_field">
                    <label htmlFor="reset-password">{t('auth.resetNewPassword')}</label>
                    <input
                      id="reset-password"
                      type="password"
                      name="password"
                      autoComplete="new-password"
                      placeholder={t('auth.resetNewPassword')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <div className="login_field">
                    <label htmlFor="reset-confirm">{t('auth.resetConfirm')}</label>
                    <input
                      id="reset-confirm"
                      type="password"
                      name="confirmPassword"
                      autoComplete="new-password"
                      placeholder={t('auth.resetRepeat')}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>
                  <button className="login_form_button" type="submit">
                    {t('auth.resetSave')}
                    <Spinner id="reset_loader" style={loader} />
                  </button>
                </form>
              </>
            ) : null}

            {done ? (
              <div className="login_auth_success" role="status">
                <p>{t('auth.resetDone')}</p>
                <Link to="/login_user" className="login_form_button login_auth_success_btn">
                  {t('auth.signInNow')}
                </Link>
              </div>
            ) : null}
          </div>
        </aside>
      </main>
      <ToastContainer />
    </div>
  );
}
